import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertService } from '../../services/alert.service';

@Component({
  selector: 'app-alert-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <!-- Background overlay -->
      <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm" 
           (click)="type !== 'confirm' ? onClose() : null"></div>

      <!-- Dialog -->
      <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div [ngClass]="dialogClasses" class="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full animate-fade-in">
          <div class="bg-white px-6 pb-4 pt-6 sm:p-8 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <!-- Icon -->
              <div [ngClass]="iconClasses" class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
                <!-- Error Icon -->
                <svg *ngIf="type === 'error'" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <!-- Warning Icon -->
                <svg *ngIf="type === 'warning'" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <!-- Info Icon -->
                <svg *ngIf="type === 'info'" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                <!-- Success Icon -->
                <svg *ngIf="type === 'success'" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <!-- Confirm Icon -->
                <svg *ngIf="type === 'confirm'" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
              </div>
              
              <!-- Content -->
              <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                <h3 class="text-lg font-semibold leading-6 text-gray-900 mb-2" id="modal-title">
                  {{ title || getDefaultTitle() }}
                </h3>
                <div class="space-y-3">
                  <p class="text-sm text-gray-600 whitespace-pre-line">{{ message }}</p>
                  
                  <!-- Details Section -->
                  <div *ngIf="details" class="mt-4">
                    <details class="group">
                      <summary class="flex cursor-pointer items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                        <svg class="mr-2 h-4 w-4 transform transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                        ดูรายละเอียด
                      </summary>
                      <div class="mt-2 max-h-48 overflow-y-auto rounded-md bg-gray-50 p-3">
                        <pre class="text-xs text-gray-700 whitespace-pre-wrap font-mono">{{ details }}</pre>
                      </div>
                    </details>
                  </div>

                  <!-- Copy Data Section -->
                  <div *ngIf="copyData && showCopyButton" class="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p class="text-xs font-medium text-gray-700 mb-2">{{ copyLabel || 'คัดลอกข้อมูล' }}:</p>
                    <div class="flex items-center space-x-2">
                      <code class="flex-1 text-xs bg-white px-3 py-2 rounded border text-gray-800 font-mono break-all">{{ copyData }}</code>
                      <button
                        type="button"
                        (click)="onCopy()"
                        class="flex-shrink-0 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        title="คัดลอก"
                      >
                        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-8">
            <!-- Confirm Dialog Buttons -->
            <div *ngIf="type === 'confirm'" class="flex space-x-3 sm:space-x-0 sm:space-x-reverse sm:space-x-3">
              <button
                type="button"
                (click)="onConfirm()"
                class="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
              >
                {{ confirmText || 'ยืนยัน' }}
              </button>
              <button
                type="button"
                (click)="onCancel()"
                class="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm transition-colors"
              >
                {{ cancelText || 'ยกเลิก' }}
              </button>
            </div>

            <!-- Single Action Button -->
            <div *ngIf="type !== 'confirm'" class="flex space-x-3">
              <button
                *ngIf="showCopyButton && copyData"
                type="button"
                (click)="onCopy()"
                class="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm transition-colors"
              >
                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {{ copyLabel || 'คัดลอก' }}
              </button>
              <button
                *ngIf="actionButton"
                type="button"
                (click)="onActionButton()"
                class="w-full inline-flex items-center justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm transition-colors"
              >
                <svg class="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
                </svg>
                {{ actionButton.text }}
              </button>
              <button
                type="button"
                (click)="onClose()"
                [ngClass]="buttonClasses"
                class="w-full inline-flex justify-center rounded-lg px-4 py-2 text-base font-medium shadow-sm sm:ml-3 sm:w-auto sm:text-sm transition-colors"
              >
                {{ buttonText || 'ตกลง' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: scale(0.95) translateY(-20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }
    
    .animate-fade-in {
      animation: fadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .backdrop-blur-sm {
      backdrop-filter: blur(4px);
    }
  `]
})
export class AlertDialogComponent {
  @Input() isOpen: boolean = false;
  @Input() type: 'error' | 'warning' | 'info' | 'success' | 'confirm' = 'error';
  @Input() title?: string;
  @Input() message: string = '';
  @Input() details?: string;
  @Input() buttonText?: string;
  @Input() confirmText?: string;
  @Input() cancelText?: string;
  @Input() showCopyButton?: boolean;
  @Input() copyData?: string;
  @Input() copyLabel?: string;
  @Input() alertId?: string;
  @Input() actionButton?: {
    text: string;
    url?: string;
    handler?: () => void;
  };
  @Output() closed = new EventEmitter<void>();
  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() copied = new EventEmitter<void>();

  constructor(private alertService: AlertService) {}

  get dialogClasses(): string {
    const baseClasses = 'relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full';
    const sizeClasses = {
      confirm: 'sm:max-w-lg',
      error: this.details ? 'sm:max-w-2xl' : 'sm:max-w-md',
      warning: 'sm:max-w-md',
      info: 'sm:max-w-md',
      success: this.copyData ? 'sm:max-w-lg' : 'sm:max-w-md'
    };
    return `${baseClasses} ${sizeClasses[this.type]}`;
  }

  get iconClasses(): string {
    const baseClasses = 'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10';
    const colorClasses = {
      error: 'bg-red-100 text-red-600',
      warning: 'bg-yellow-100 text-yellow-600',
      info: 'bg-blue-100 text-blue-600',
      success: 'bg-green-100 text-green-600',
      confirm: 'bg-orange-100 text-orange-600'
    };
    return `${baseClasses} ${colorClasses[this.type]}`;
  }

  get buttonClasses(): string {
    const baseClasses = 'w-full inline-flex justify-center rounded-lg px-4 py-2 text-base font-medium shadow-sm sm:ml-3 sm:w-auto sm:text-sm transition-colors';
    const colorClasses = {
      error: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white',
      warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500 text-white',
      info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 text-white',
      success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white',
      confirm: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500 text-white'
    };
    return `${baseClasses} ${colorClasses[this.type]} focus:outline-none focus:ring-2 focus:ring-offset-2`;
  }

  getDefaultTitle(): string {
    const titles = {
      error: 'เกิดข้อผิดพลาด',
      warning: 'คำเตือน',
      info: 'ข้อมูล',
      success: 'สำเร็จ',
      confirm: 'ยืนยันการดำเนินการ'
    };
    return titles[this.type];
  }

  onClose(): void {
    if (this.alertId) {
      this.alertService.handleAction({ id: this.alertId, action: 'close' });
    }
    this.closed.emit();
  }

  onConfirm(): void {
    if (this.alertId) {
      this.alertService.handleAction({ id: this.alertId, action: 'confirm' });
    }
    this.confirmed.emit();
  }

  onCancel(): void {
    if (this.alertId) {
      this.alertService.handleAction({ id: this.alertId, action: 'cancel' });
    }
    this.cancelled.emit();
  }

  async onCopy(): Promise<void> {
    if (this.copyData) {
      const success = await this.alertService.copyToClipboard(this.copyData);
      if (success) {
        this.alertService.showSuccess('คัดลอกข้อมูลสำเร็จ!', undefined, 2000);
        if (this.alertId) {
          this.alertService.handleAction({ id: this.alertId, action: 'copy', data: this.copyData });
        }
        this.copied.emit();
      } else {
        this.alertService.showError('ไม่สามารถคัดลอกข้อมูลได้');
      }
    }
  }

  onActionButton(): void {
    if (this.actionButton) {
      // Open URL in new tab if provided
      if (this.actionButton.url) {
        window.open(this.actionButton.url, '_blank');
      }

      // Execute handler if provided
      if (this.actionButton.handler) {
        this.actionButton.handler();
      }
    }
  }
}